// frontend/src/pages/Roles/GerenciarPerfis.jsx

import React, { useState, useEffect } from "react";
import Template from "../../../components/Template";
import api from "../../../services/api";
import ConfirmationModal from "../../../components/ConfirmationModal";
import SuccessModal from "../../../components/SuccessModal";
import { useNavigate } from "react-router-dom";
import usePermission from "../../../hooks/usePermission";

const GerenciarPerfis = () => {
    const navigate = useNavigate();

    // Lista de roles
    const [roleList, setRoleList] = useState([]);

    // Lista de permissões
    const [permissionList, setPermissionList] = useState([]);
    const [groupedPermissions, setGroupedPermissions] = useState({});

    // Formulário
    const [formData, setFormData] = useState({
        id: null,
        nome: "",
        permissions: [], // array de IDs
    });

    // Controle do formulário
    const [isEditMode, setIsEditMode] = useState(false);
    const podeAtualizar = usePermission("ATUALIZAR_PERFIL");
    const podeDeletar = usePermission("DELETAR_PERFIL");

    // Modais
    const [showConfirmDialog, setShowConfirmDialog] = useState(false);
    const [showConfirmDelete, setShowConfirmDelete] = useState(false);
    const [roleToDelete, setRoleToDelete] = useState(null);
    const [showSuccessDialog, setShowSuccessDialog] = useState(false);

    // Carregar roles e permissões
    useEffect(() => {
        fetchRoles();
        fetchPermissions();
    }, []);

    const fetchRoles = async () => {
        try {
            const response = await api.get("/roles");
            setRoleList(response.data);
        } catch (error) {
            console.error("Erro ao carregar roles:", error);
            alert("Erro ao carregar roles. Tente novamente.");
        }
    };

    const fetchPermissions = async () => {
        try {
            const response = await api.get("/permissions");
            const grouped = response.data.reduce((acc, perm) => {
                const category = perm.nome.split("_")[1] || "Outros";
                if (!acc[category]) acc[category] = [];
                acc[category].push(perm);
                return acc;
            }, {});
            setPermissionList(response.data);
            setGroupedPermissions(grouped);
        } catch (error) {
            console.error("Erro ao carregar permissões:", error);
        }
    };

    // Manipulador de mudança nos inputs
    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setFormData({ ...formData, [name]: value });
    };

    // Manipulador de mudança nas permissões (checkboxes)
    const handlePermissionChange = (permissionId) => {
        setFormData((prev) => {
            if (prev.permissions.includes(permissionId)) {
                return {
                    ...prev,
                    permissions: prev.permissions.filter((id) => id !== permissionId),
                };
            } else {
                return {
                    ...prev,
                    permissions: [...prev.permissions, permissionId],
                };
            }
        });
    };

    const toggleCategoryPermissions = (category) => {
        const categoryPermissions = groupedPermissions[category].map((p) => p.id);
        setFormData((prev) => {
            const allSelected = categoryPermissions.every((id) => prev.permissions.includes(id));
            return {
                ...prev,
                permissions: allSelected
                    ? prev.permissions.filter((id) => !categoryPermissions.includes(id))
                    : [...new Set([...prev.permissions, ...categoryPermissions])],
            };
        });
    };

    const toggleAllPermissions = () => {
        const allPermissions = permissionList.map((p) => p.id);
        const allSelected = allPermissions.every((id) => formData.permissions.includes(id));
        setFormData({
            ...formData,
            permissions: allSelected ? [] : allPermissions,
        });
    };

    // Submeter o formulário
    const handleSubmit = (e) => {
        e.preventDefault();
        if (!formData.nome) {
            alert("O nome do perfil é obrigatório.");
            return;
        }
        setShowConfirmDialog(true);
    };

    // Confirmar criação/edição
    const handleConfirmSubmit = async () => {
        setShowConfirmDialog(false);
        try {
            if (isEditMode) {
                // Atualizar role existente
                await api.put(`/roles/${formData.id}`, {
                    nome: formData.nome,
                    permissions: formData.permissions,
                });
            } else {
                // Criar nova role
                await api.post("/roles", {
                    nome: formData.nome,
                    permissions: formData.permissions,
                });
            }

            // Atualizar lista de roles
            fetchRoles();

            // Mostrar sucesso
            setShowSuccessDialog(true);

            // Limpar formulário
            setFormData({
                id: null,
                nome: "",
                permissions: [],
            });
            setIsEditMode(false);
        } catch (error) {
            console.error("Erro ao salvar perfil:", error);
            alert(
                `Erro ao ${isEditMode ? "atualizar" : "cadastrar"} o perfil. Verifique os dados e tente novamente.`
            );
        }
    };

    const handleCancelSubmit = () => {
        setShowConfirmDialog(false);
    };

    // Editar role
    const handleEdit = (role) => {
        setIsEditMode(true);
        setFormData({
            id: role.id,
            nome: role.nome,
            permissions: role.permissions.map((perm) => perm.id),
        });
        window.scrollTo({ top: 0, behavior: "smooth" });
    };

    // Deletar role
    const handleDelete = (role) => {
        setRoleToDelete(role);
        setShowConfirmDelete(true);
    };

    const handleConfirmDelete = async () => {
        setShowConfirmDelete(false);
        try {
            await api.delete(`/roles/${roleToDelete.id}`);
            // Atualizar lista de roles
            fetchRoles();
            setRoleToDelete(null);
            setShowSuccessDialog(true);
        } catch (error) {
            console.error("Erro ao deletar perfil:", error);
            alert("Erro ao deletar a perfil. Verifique se ela está em uso e tente novamente.");
        }
    };

    const handleCancelDelete = () => {
        setShowConfirmDelete(false);
        setRoleToDelete(null);
    };

    // Cancelar edição
    const handleCancelEdit = () => {
        setIsEditMode(false);
        setFormData({
            id: null,
            nome: "",
            permissions: [],
        });
    };

    const handleBack = () => {
        navigate('/configuracoes');
    };

    const ehAdminEdicao = isEditMode && formData.nome === "ADMIN";

    return (
        <Template>
            <div className="w-full px-4 py-6 flex flex-col gap-8">
                {/* Título da Página */}
                <div>
                    <h1 className="text-2xl text-stone-800 font-medium">Gerenciar Perfis</h1>
                </div>

                {/* Formulário de Criação/Edição */}
                <form
                    onSubmit={handleSubmit}
                    className="flex flex-col gap-4 bg-white p-6 rounded-md border border-slate-200"
                >
                    <div className="text-stone-600 text-base font-medium">
                        {isEditMode ? "Editar Perfil" : "Cadastrar Novo Perfil"}
                    </div>

                    {ehAdminEdicao && (
                        <div className="bg-amber-50 border border-amber-200 text-amber-800 rounded-md p-3 text-sm">
                            🔒 O perfil <strong>ADMIN</strong> é protegido: não pode ser renomeado, deletado, e suas
                            permissões são automaticamente sincronizadas com todas do sistema. Alterações nesses campos
                            serão ignoradas.
                        </div>
                    )}

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        {/* Nome da Role */}
                        <div className="flex flex-col gap-1.5">
                            <label className="text-sm text-slate-700 font-medium">Nome</label>
                            <input
                                type="text"
                                name="nome"
                                value={formData.nome}
                                onChange={handleInputChange}
                                disabled={ehAdminEdicao}
                                className={`px-3 py-2 border border-slate-300 rounded-md text-sm ${ehAdminEdicao ? "bg-slate-100 text-slate-500 cursor-not-allowed" : ""}`}
                                placeholder="Digite o nome do perfil"
                                required
                            />
                        </div>

                        {/* Permissões */}
                        <div className="flex flex-col gap-1.5">
                            <label className="text-sm text-slate-700 font-medium">Permissões</label>
                            <div className="flex flex-col gap-2 border border-slate-300 rounded-md p-4 max-h-64 overflow-y-auto">

                                <label className="inline-flex items-center gap-1 text-sm font-medium">
                                    <input type="checkbox" onChange={toggleAllPermissions} disabled={ehAdminEdicao} />
                                    Selecionar Todas
                                </label>
                                {Object.keys(groupedPermissions).map((category) => (
                                    <div key={category} className="border-b py-2">
                                        <label className="font-medium text-sm cursor-pointer inline-flex items-center">
                                            <input type="checkbox" onChange={() => toggleCategoryPermissions(category)} disabled={ehAdminEdicao} />
                                            {category}
                                        </label>
                                        <div className="flex flex-wrap gap-2 mt-2">
                                            {groupedPermissions[category].map((permission) => (
                                                <label key={permission.id} className={`inline-flex items-center gap-1 text-sm ${ehAdminEdicao ? "text-slate-500" : ""}`}>
                                                    <input
                                                        type="checkbox"
                                                        checked={ehAdminEdicao ? true : formData.permissions.includes(permission.id)}
                                                        onChange={() => handlePermissionChange(permission.id)}
                                                        disabled={ehAdminEdicao}
                                                    />
                                                    {permission.descricao}
                                                </label>
                                            ))}
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>
                    </div>

                    {/* Botões do Formulário */}
                    <div className="flex items-center gap-4">
                        {isEditMode && (
                            <button
                                type="button"
                                onClick={handleCancelEdit}
                                className="px-4 py-2 bg-stone-400 text-white text-sm font-medium rounded-md"
                            >
                                Cancelar
                            </button>
                        )}

                        {usePermission('CRIAR_PERFIL') && (
                            <button
                                type="submit"
                                className="px-4 py-2 bg-stone-600 text-white text-sm font-medium rounded-md"
                            >
                                {isEditMode ? "Salvar Alterações" : "Adicionar Perfil"}
                            </button>
                        )}
                    </div>
                </form>

                {/* Lista de Roles */}
                <div className="bg-white p-6 rounded-md border border-slate-200">
                    <div className="text-stone-600 text-base font-medium mb-4">
                        Lista de Perfis
                    </div>

                    <div className="overflow-x-auto">
                        <table className="min-w-full text-left border-collapse">
                            <thead>
                                <tr className="bg-stone-100">
                                    <th className="px-4 py-2 border border-slate-200">ID</th>
                                    <th className="px-4 py-2 border border-slate-200">Nome</th>
                                    <th className="px-4 py-2 border border-slate-200">Permissões</th>
                                    <th className="px-4 py-2 border border-slate-200">Ações</th>
                                </tr>
                            </thead>
                            <tbody>
                                {roleList.length > 0 ? (
                                    roleList.map((role) => (
                                        <tr key={role.id} className="border-b">
                                            <td className="px-4 py-2 border border-slate-200">{role.id}</td>
                                            <td className="px-4 py-2 border border-slate-200">{role.nome}</td>
                                            <td className="px-4 py-2 border border-slate-200">
                                                {role.permissions.map((perm) => perm.descricao).join(", ")}
                                            </td>
                                            <td className="px-4 py-2 border border-slate-200">
                                                {/* Botão Editar */}
                                                {podeAtualizar && (
                                                    <button
                                                        type="button"
                                                        onClick={() => handleEdit(role)}
                                                        className="px-2 py-1 bg-stone-600 text-white rounded-md text-sm mr-2"
                                                    >
                                                        Editar
                                                    </button>
                                                )}
                                                {/* Botão Deletar */}
                                                {podeDeletar && role.nome !== "ADMIN" && (
                                                    <button
                                                        type="button"
                                                        onClick={() => handleDelete(role)}
                                                        className="px-2 py-1 bg-red-600 text-white rounded-md text-sm"
                                                    >
                                                        Deletar
                                                    </button>
                                                )}
                                                {role.nome === "ADMIN" && (
                                                    <span className="ml-2 text-xs text-amber-700" title="Perfil protegido">
                                                        🔒 protegido
                                                    </span>
                                                )}
                                            </td>
                                        </tr>
                                    ))
                                ) : (
                                    <tr>
                                        <td
                                            colSpan={4}
                                            className="px-4 py-2 text-center text-slate-700"
                                        >
                                            Nenhum perfil cadastrado.
                                        </td>
                                    </tr>
                                )}
                            </tbody>
                        </table>
                    </div>
                </div>
                {/* Botões de Ação */}
                <div className="flex items-center w-full gap-4">
                    <div className="flex gap-4 ml-auto">
                        <button
                            type="button"
                            className="px-4 py-2 bg-stone-600 text-white text-sm font-medium rounded-md flex items-center gap-2"
                            onClick={handleBack}
                        >
                            <img className="w-4 h-4" src="/images/icon/arrow-left.png" alt="Voltar" />
                            <span>Voltar</span>
                        </button>

                    </div>
                </div>
            </div>

            {/* Modais de Confirmação */}
            <ConfirmationModal
                isVisible={showConfirmDialog}
                onConfirm={handleConfirmSubmit}
                onCancel={handleCancelSubmit}
                message={`Deseja ${isEditMode ? "salvar as alterações" : "adicionar este perfil"}?`}
            />

            <ConfirmationModal
                isVisible={showConfirmDelete}
                onConfirm={handleConfirmDelete}
                onCancel={handleCancelDelete}
                message={`Deseja deletar o perfil "${roleToDelete?.descricao}"?`}
                textButtonConfirm="Sim, Deletar"
            />

            {/* Modal de Sucesso */}
            <SuccessModal
                isVisible={showSuccessDialog}
                message={`Perfil ${isEditMode ? "atualizado" : "adicionado"} com sucesso!`}
                onClose={() => setShowSuccessDialog(false)}
            />
        </Template>
    );
};

export default GerenciarPerfis;
